import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SmsOptInForm from '../components/SmsOptInForm';
import { BRAND_NAME, SAMPLE_MESSAGE, CONTACT_EMAIL } from '../config/brand';

// Standalone opt-in page. Deliberately its own URL and its own document: SMS
// consent must not be bundled into the mandatory Terms of Service (Twilio
// error 30923). The program disclosures are repeated here in full so a carrier
// reviewer sees them on this page without following a link.
const SmsOptIn = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `SMS Opt-In - ${BRAND_NAME}`,
    url: 'https://lajdigital.com/sms-optin',
    description: `Optional SMS text messaging sign-up for ${BRAND_NAME}. Consent is not a condition of purchase.`,
  };

  return (
    <section className="pt-32 pb-24 relative bg-gray-950 min-h-screen">
      <Helmet>
        <title>SMS Opt-In | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`Sign up to receive optional SMS text message updates from ${BRAND_NAME}. Consent is not required to use our services. Message and data rates may apply. Reply STOP to cancel, HELP for help.`}
        />
        <link rel="canonical" href="https://lajdigital.com/sms-optin" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="container-custom max-w-4xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel p-8 md:p-12 rounded-3xl text-gray-300 font-light space-y-8"
        >
          <div className="border-b border-gray-800 pb-8 mb-8">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              SMS Text Messaging Sign-Up
            </h1>
            <p className="text-gray-400">
              Optional text message updates from {BRAND_NAME}. Signing up takes a few seconds and you can
              cancel at any time.
            </p>
          </div>

          <div className="p-6 bg-purple-900/10 border-l-2 border-purple-500 rounded-r-xl">
            <h2 className="text-lg font-display font-bold text-purple-300 mb-3">
              Consent Is Not a Condition of Service
            </h2>
            <p className="text-gray-200">
              Opting in to receive text messages from {BRAND_NAME} is not required to use this website,
              contact us, purchase or receive any product or service, create an account, or become a
              client. You may decline SMS messaging and still access everything we offer. If you have
              opted in, you may opt out at any time without any effect on your ability to do business
              with us.
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-xl font-display font-bold text-white">SMS Program Terms</h2>
            <div className="p-6 bg-gray-900/50 rounded-xl space-y-4 border border-gray-800">
              <p>
                By providing your mobile phone number and opting in below, you agree to receive text
                messages from {BRAND_NAME} related to your inquiries, account updates, appointments, and
                service notifications. Messages are sent by {BRAND_NAME}. We may use automated systems
                and third-party messaging providers to deliver them on our behalf.
              </p>
              <ul className="list-disc pl-5 space-y-3 text-gray-200">
                <li>
                  <strong className="text-white">Program Description:</strong> Messages may include
                  service updates, responses to inquiries, appointment reminders, and customer support
                  communications.
                </li>
                <li>
                  <strong className="text-white">Message Frequency:</strong> Message frequency varies
                  based on your interaction with our services.
                </li>
                <li>
                  <strong className="text-white">Pricing Disclosure:</strong>{' '}
                  <span className="text-purple-300 font-medium">Message and data rates may apply.</span>{' '}
                  Please check with your mobile carrier for details regarding your specific plan.
                </li>
                <li>
                  <strong className="text-white">Opt-Out Instructions:</strong> You can cancel the SMS
                  service at any time.{' '}
                  <span className="text-purple-300 font-medium">Reply &quot;STOP&quot; to cancel.</span>{' '}
                  After you send the SMS message &quot;STOP&quot; to us, we will send you an SMS message
                  to confirm that you have been unsubscribed. After this, you will no longer receive SMS
                  messages from us.
                </li>
                <li>
                  <strong className="text-white">Help Instructions:</strong> If you are experiencing
                  issues with the messaging program you can{' '}
                  <span className="text-purple-300 font-medium">
                    reply with the keyword &quot;HELP&quot; for more assistance
                  </span>
                  , or you can get help directly by emailing us at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-400 hover:text-purple-300 underline">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </li>
                <li>
                  <strong className="text-white">Carrier Liability:</strong> Carriers are not liable for
                  delayed or undelivered messages.
                </li>
                <li>
                  <strong className="text-white">Sample Message:</strong>{' '}
                  <span className="text-gray-300 italic">&quot;{SAMPLE_MESSAGE}&quot;</span>
                </li>
              </ul>
              <p className="text-sm text-gray-400">
                Full details are in our{' '}
                <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>

          <SmsOptInForm />
        </motion.div>
      </div>
    </section>
  );
};

export default SmsOptIn;
